<template>
  <div class="forum-wrapper">
    <div class="forum-layout">
      <!-- Main Column -->
      <div class="main-column">
        <!-- User Activity Header -->
        <header class="activity-header">
          <h1 class="typo-headline-4">Mi Actividad en el Foro</h1>
        </header>

        <!-- Tabs for different activity types -->
        <div class="activity-tabs">
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'threads' }"
            @click="activeTab = 'threads'"
          >
            Mis Hilos <span class="count">({{ threadsPagination.total || 0 }})</span>
          </button>
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'comments' }"
            @click="activeTab = 'comments'"
          >
            Mis Comentarios <span class="count">({{ commentsPagination.total || 0 }})</span>
          </button>
          <button 
            class="tab-button typo-button" 
            :class="{ active: activeTab === 'favorites' }"
            @click="activeTab = 'favorites'"
          >
            Favoritos <span class="count">({{ favoritesPagination.total || 0 }})</span>
          </button>
        </div>

        <!-- Loading and Error States -->
        <div v-if="loading" class="loading-message typo-body-2">
          Cargando tu actividad...
        </div>
        <div v-else-if="error" class="error-message typo-body-2">
          {{ error }}
        </div>
        <div v-else>
          <!-- Threads Tab Content -->
          <div v-if="activeTab === 'threads'" class="tab-content">
            <div v-if="threads.length === 0" class="empty-state">
              <p class="typo-body-1">No has creado ningún hilo todavía.</p>
              <button class="create-thread-btn typo-button" @click="createNewThread">
                <i class="material-icons">add</i>
                Crear Nuevo Hilo
              </button>
            </div>
            <div v-else class="threads-list">
              <router-link 
                v-for="thread in threads" 
                :key="thread.id" 
                :to="{ name: 'ForumThread', params: { nid: thread.id } }"
                class="thread-item clickable-thread"
              >
                <div class="thread-main">
                  <h3 class="thread-title typo-subtitle-1">
                    {{ thread.title }}
                  </h3>
                  <div class="thread-meta typo-caption">
                    <span class="thread-date">{{ formatDate(thread.created) }}</span>
                    <span class="separator">•</span>
                    <span class="comment-count">{{ thread.commentCount }} comentarios</span>
                  </div>
                </div>
              </router-link>
              
              <!-- Pagination for threads -->
              <div class="pagination" v-if="threadsPagination.pages > 1">
                <button 
                  class="pagination-btn" 
                  :disabled="threadsPage === 0" 
                  @click="prevThreadsPage"
                >
                  Anterior
                </button>
                <span class="page-info">
                  Página {{ threadsPage + 1 }} de {{ threadsPagination.pages }}
                </span>
                <button 
                  class="pagination-btn" 
                  :disabled="threadsPage >= threadsPagination.pages - 1" 
                  @click="nextThreadsPage"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          <!-- Comments Tab Content -->
          <div v-if="activeTab === 'comments'" class="tab-content">
            <div v-if="comments.length === 0" class="empty-state">
              <p class="typo-body-1">No has publicado ningún comentario todavía.</p>
            </div>
            <div v-else class="comments-list">
              <router-link 
                v-for="comment in comments" 
                :key="comment.id" 
                :to="'/app' + comment.url"
                class="comment-item clickable-comment"
              >
                <div class="comment-main">
                  <h3 class="comment-thread-title typo-subtitle-1">
                    {{ comment.threadTitle }}
                  </h3>
                  <div class="comment-content typo-body-2">
                    <p v-if="comment.subject" class="comment-subject">{{ comment.subject }}</p>
                    <p class="comment-body">{{ comment.body }}</p>
                  </div>
                  <div class="comment-meta typo-caption">
                    <span class="comment-date">{{ formatDate(comment.created) }}</span>
                  </div>
                </div>
              </router-link>
              
              <!-- Pagination for comments -->
              <div class="pagination" v-if="commentsPagination.pages > 1">
                <button 
                  class="pagination-btn" 
                  :disabled="commentsPage === 0" 
                  @click="prevCommentsPage"
                >
                  Anterior
                </button>
                <span class="page-info">
                  Página {{ commentsPage + 1 }} de {{ commentsPagination.pages }}
                </span>
                <button 
                  class="pagination-btn" 
                  :disabled="commentsPage >= commentsPagination.pages - 1" 
                  @click="nextCommentsPage"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>

          <!-- Favorites Tab Content -->
          <div v-if="activeTab === 'favorites'" class="tab-content">
            <div v-if="favorites.length === 0" class="empty-state">
              <p class="typo-body-1">No has marcado ningún hilo como favorito todavía.</p>
              <p class="typo-body-2">Puedes marcar hilos como favoritos para acceder a ellos fácilmente más tarde.</p>
            </div>
            <div v-else class="threads-list">
              <router-link 
                v-for="thread in favorites" 
                :key="thread.id" 
                :to="{ name: 'ForumThread', params: { nid: thread.id } }"
                class="thread-item clickable-thread"
              >
                <div class="thread-main">
                  <h3 class="thread-title typo-subtitle-1">
                    {{ thread.title }}
                  </h3>
                  <div class="thread-meta typo-caption">
                    <span class="thread-author">Por: {{ thread.author }}</span>
                    <span class="separator">•</span>
                    <span class="thread-date">{{ formatDate(thread.created) }}</span>
                    <span class="separator">•</span>
                    <span class="comment-count">{{ thread.commentCount }} comentarios</span>
                  </div>
                </div>
              </router-link>
              
              <!-- Pagination for favorites -->
              <div class="pagination" v-if="favoritesPagination.pages > 1">
                <button 
                  class="pagination-btn" 
                  :disabled="favoritesPage === 0" 
                  @click="prevFavoritesPage"
                >
                  Anterior
                </button>
                <span class="page-info">
                  Página {{ favoritesPage + 1 }} de {{ favoritesPagination.pages }}
                </span>
                <button 
                  class="pagination-btn" 
                  :disabled="favoritesPage >= favoritesPagination.pages - 1" 
                  @click="nextFavoritesPage"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Side Column -->
      <div class="side-column">
        <!-- Navigation Links -->
        <navigation-links />
        
        <!-- User Activity Widget -->
        <user-activity-widget />
        
        <!-- Recent Content -->
        <recent-content-container />
        
        <!-- Trending Topics -->
        <trending-topics-container />
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
import { mapGetters } from 'vuex';
import NavigationLinks from '../components/NavigationLinks.vue';
import UserActivityWidget from '../components/UserActivityWidget.vue';
import RecentContentContainer from '../components/RecentContentContainer.vue';
import TrendingTopicsContainer from '../components/TrendingTopicsContainer.vue';

const axios = forumApi;

export default {
  name: 'UserActivity',
  components: {
    NavigationLinks,
    UserActivityWidget,
    RecentContentContainer,
    TrendingTopicsContainer
  },
  data() {
    return {
      activeTab: 'threads',
      threads: [],
      comments: [],
      favorites: [],
      loading: true,
      error: null,
      threadsPage: 0,
      commentsPage: 0,
      favoritesPage: 0,
      itemsPerPage: 10,
      threadsPagination: {
        total: 0,
        pages: 0
      },
      commentsPagination: {
        total: 0,
        pages: 0
      },
      favoritesPagination: {
        total: 0,
        pages: 0
      }
    };
  },
  computed: {
    ...mapGetters(['_g_IsUserLogged']),
    isLoggedIn() {
      return this._g_IsUserLogged;
    }
  },
  watch: {
    activeTab(newTab) {
      // When tab changes, update URL query parameter
      this.$router.replace({ 
        query: { ...this.$route.query, tab: newTab } 
      });
    }
  },
  mounted() {
    // Check if user is logged in
    if (!this.isLoggedIn) {
      this.$router.push('/forum');
      return;
    }
    
    // Set active tab from URL if present
    if (this.$route.query.tab && ['threads', 'comments', 'favorites'].includes(this.$route.query.tab)) {
      this.activeTab = this.$route.query.tab;
    }
    
    // Load initial data
    this.fetchUserActivity();
    
    // Listen for tab change events from UserActivityWidget
    this.$root.$on('changeUserActivityTab', this.handleTabChange);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('changeUserActivityTab', this.handleTabChange);
  },
  methods: {
    fetchUserActivity() {
      this.loading = true;
      this.error = null;
      
      // Create an array of promises for parallel requests
      const promises = [
        this.fetchThreads(),
        this.fetchComments(),
        this.fetchFavorites()
      ];
      
      // Wait for all requests to complete
      Promise.all(promises)
        .then(() => {
          this.loading = false;
        })
        .catch(err => {
          console.error('Error fetching user activity:', err);
          this.error = 'Error al cargar tu actividad';
          this.loading = false;
        });
    },
    
    fetchThreads() {
      return axios.get(`/forum/api/user/threads?page=${this.threadsPage}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data) {
            this.threads = response.data.threads || [];
            this.threadsPagination = response.data.pagination || { total: 0, pages: 0 };
          }
        });
    },
    
    fetchComments() {
      return axios.get(`/forum/api/user/comments?page=${this.commentsPage}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data) {
            this.comments = response.data.comments || [];
            this.commentsPagination = response.data.pagination || { total: 0, pages: 0 };
          }
        });
    },
    
    fetchFavorites() {
      return axios.get(`/forum/api/user/favorites?page=${this.favoritesPage}&limit=${this.itemsPerPage}`)
        .then(response => {
          if (response.data && response.data.threads) {
            this.favorites = response.data.threads;
            this.favoritesPagination = response.data.pagination;
          } else {
            throw new Error('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching favorites:', err);
          this.error = 'Error al cargar tus hilos favoritos';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    nextThreadsPage() {
      if (this.threadsPage < this.threadsPagination.pages - 1) {
        this.threadsPage++;
        this.fetchThreads();
      }
    },
    
    prevThreadsPage() {
      if (this.threadsPage > 0) {
        this.threadsPage--;
        this.fetchThreads();
      }
    },
    
    nextCommentsPage() {
      if (this.commentsPage < this.commentsPagination.pages - 1) {
        this.commentsPage++;
        this.fetchComments();
      }
    },
    
    prevCommentsPage() {
      if (this.commentsPage > 0) {
        this.commentsPage--;
        this.fetchComments();
      }
    },
    
    nextFavoritesPage() {
      if (this.favoritesPage < this.favoritesPagination.pages - 1) {
        this.favoritesPage++;
        this.fetchFavorites();
      }
    },
    
    prevFavoritesPage() {
      if (this.favoritesPage > 0) {
        this.favoritesPage--;
        this.fetchFavorites();
      }
    },
    
    formatDate(timestamp) {
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    createNewThread() {
      this.$router.push({ name: 'ForumNewThread' });
    },
    
    handleTabChange(newTab) {
      // Change the active tab when event is received from UserActivityWidget
      if (['threads', 'comments', 'favorites'].includes(newTab) && newTab !== this.activeTab) {
        this.activeTab = newTab;
      }
    }
  }
};
</script>

<style scoped>
.forum-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.forum-layout {
  display: flex;
  gap: 20px;
}

.main-column {
  flex: 1;
  min-width: 0; /* Prevents flex items from overflowing */
}

.side-column {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.activity-header {
  color: var(--color-text-primary);
  margin-bottom: 20px;
}

.activity-tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--color-outline);
}

.tab-button {
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.tab-button.active {
  color: var(--color-highlight);
  border-bottom-color: var(--color-highlight);
}

.count {
  color: var(--color-text-secondary);
  font-size: 0.9em;
}

.tab-content {
  background-color: var(--color-surface);
  border-radius: 8px;
  padding: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: var(--color-text-primary);
}

.empty-state p {
  color: var(--color-text-primary);
}

.empty-state .typo-body-2 {
  color: var(--color-text-secondary);
}

.create-thread-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  margin: 20px auto 0;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.create-thread-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.thread-item, .comment-item {
  padding: 15px;
  border-bottom: 1px solid var(--color-outline);
}

.thread-item:last-child, .comment-item:last-child {
  border-bottom: none;
}

.clickable-thread, .clickable-comment {
  display: block;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease, transform 0.1s ease;
  border-radius: 4px;
}

.clickable-thread:hover, .clickable-comment:hover {
  background-color: var(--color-active-surface, rgba(0, 0, 0, 0.04));
  text-decoration: none;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.clickable-thread .thread-title, .clickable-comment .comment-thread-title {
  color: var(--color-highlight);
  margin: 0;
}

.clickable-thread:hover .thread-title, .clickable-comment:hover .comment-thread-title {
  color: var(--color-highlight-dark, #1976d2);
}

.thread-meta, .comment-meta {
  display: flex;
  align-items: center;
  color: var(--color-text-secondary);
  margin-top: 5px;
}

.separator {
  margin: 0 8px;
}

.comment-content {
  margin: 10px 0;
}

.comment-subject {
  font-weight: 500;
  margin-bottom: 5px;
}

.comment-body {
  color: var(--color-text-primary);
  margin: 0;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 15px;
}

.pagination-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: 1px solid var(--color-outline);
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-active-surface);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: var(--color-text-secondary);
}

.loading-message,
.error-message {
  padding: 20px;
  background-color: var(--color-surface);
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
  color: var(--color-text-primary);
}

.error-message {
  border-left: 4px solid #f44336;
  color: #f44336;
}

/* Responsive Design */
@media (max-width: 768px) {
  .forum-layout {
    flex-direction: column;
  }
  
  .main-column,
  .side-column {
    width: 100%;
  }
  
  .side-column {
    margin-top: 20px;
  }
}
</style> 